import type { BackendModule, ReadCallback, ResourceKey } from 'i18next';
import type { IAppTranslationRepository } from './i18n-repository.interface';

/**
 * i18next Backend Module that uses our repository pattern
 * Bridges i18next's backend interface with our clean architecture
 * 
 * This adapter allows i18next to use any implementation of IAppTranslationRepository,
 * making it easy to swap between local files, remote APIs, or any other source.
 */
export class RepositoryBackend implements BackendModule<object> {
  type = 'backend' as const;

  private repository: IAppTranslationRepository;

  constructor(repository: IAppTranslationRepository) {
    this.repository = repository;
  }

  /**
   * Initialize the backend
   * Called by i18next during initialization
   */
  init(): void {
    // No initialization needed - repository is injected via constructor
  }

  /**
   * Read translations for a language and namespace
   * Called by i18next when translations are needed
   * 
   * @param language - The language code (e.g., 'en', 'he')
   * @param namespace - The namespace (e.g., 'common', 'assignEv')
   * @param callback - Callback to return data or error
   */
  read(language: string, namespace: string, callback: ReadCallback): void {
    this.repository
      .loadTranslations(language, namespace)
      .then((resources) => {
        callback(null, resources as ResourceKey);
      })
      .catch((error) => {
        callback(error, null);
      });
  }

  /**
   * Factory method to create the backend
   * @param repository - The translation repository to use
   */
  static create(repository: IAppTranslationRepository): RepositoryBackend {
    return new RepositoryBackend(repository);
  }
}
